import React from 'react';
import { Input, Button, DatePicker, Radio, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import type { Dayjs } from 'dayjs';

interface QueryParamsProps {
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  docNoFrom: string;
  docNoTo: string;
  deptFrom: string;
  deptTo: string;
  vendorFrom: string;
  vendorTo: string;
  needFrom: Dayjs | null;
  needTo: Dayjs | null;
  chkDate1From: Dayjs | null;
  chkDate1To: Dayjs | null;
  chkDate2From: Dayjs | null;
  chkDate2To: Dayjs | null;
  entryEmpFrom: string;
  entryEmpTo: string;
  approvalStatus: number;
  reportTypes: string[];
  loading: boolean;
  onDateFromChange: (v: Dayjs | null) => void;
  onDateToChange: (v: Dayjs | null) => void;
  onDocNoFromChange: (v: string) => void;
  onDocNoToChange: (v: string) => void;
  onDeptFromChange: (v: string) => void;
  onDeptToChange: (v: string) => void;
  onVendorFromChange: (v: string) => void;
  onVendorToChange: (v: string) => void;
  onNeedFromChange: (v: Dayjs | null) => void;
  onNeedToChange: (v: Dayjs | null) => void;
  onChkDate1FromChange: (v: Dayjs | null) => void;
  onChkDate1ToChange: (v: Dayjs | null) => void;
  onChkDate2FromChange: (v: Dayjs | null) => void;
  onChkDate2ToChange: (v: Dayjs | null) => void;
  onEntryEmpFromChange: (v: string) => void;
  onEntryEmpToChange: (v: string) => void;
  onApprovalStatusChange: (v: number) => void;
  onReportTypesChange: (v: string[]) => void;
  onQuery: () => void;
}

const LABEL_W = 120;

const QueryParams: React.FC<QueryParamsProps> = (props) => {
  const {
    dateFrom, dateTo, docNoFrom, docNoTo, deptFrom, deptTo,
    vendorFrom, vendorTo, needFrom, needTo, chkDate1From, chkDate1To,
    chkDate2From, chkDate2To, entryEmpFrom, entryEmpTo,
    approvalStatus, reportTypes, loading,
    onDateFromChange, onDateToChange, onDocNoFromChange, onDocNoToChange,
    onDeptFromChange, onDeptToChange, onVendorFromChange, onVendorToChange,
    onNeedFromChange, onNeedToChange, onChkDate1FromChange, onChkDate1ToChange,
    onChkDate2FromChange, onChkDate2ToChange, onEntryEmpFromChange, onEntryEmpToChange,
    onApprovalStatusChange, onReportTypesChange, onQuery,
  } = props;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>請款日期[從]</span>
          <DatePicker value={dateFrom} onChange={onDateFromChange} style={{ width: 140 }} />
          <span className="text-sm">[至]</span>
          <DatePicker value={dateTo} onChange={onDateToChange} style={{ width: 140 }} />
        </div>
      </div>

      {/* Row 2: DocNo range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>請款單號[從]</span>
          <Input value={docNoFrom} onChange={e => onDocNoFromChange(e.target.value.toUpperCase())}
            placeholder="起" style={{ width: 100 }} maxLength={6} />
          <span className="text-sm">[至]</span>
          <Input value={docNoTo} onChange={e => onDocNoToChange(e.target.value.toUpperCase())}
            placeholder="迄" style={{ width: 100 }} maxLength={6} />
        </div>
      </div>

      {/* Row 3: Dept range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>歸屬部門[從]</span>
          <Input value={deptFrom} onChange={e => onDeptFromChange(e.target.value.toUpperCase())}
            placeholder="起" style={{ width: 80 }} maxLength={4} />
          <span className="text-sm">[至]</span>
          <Input value={deptTo} onChange={e => onDeptToChange(e.target.value.toUpperCase())}
            placeholder="迄" style={{ width: 80 }} maxLength={4} />
        </div>
      </div>

      {/* Row 4: Vendor/Emp range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>廠商/員工[從]</span>
          <Input value={vendorFrom} onChange={e => onVendorFromChange(e.target.value)}
            placeholder="起" style={{ width: 140 }} maxLength={12} />
          <span className="text-sm">[至]</span>
          <Input value={vendorTo} onChange={e => onVendorToChange(e.target.value)}
            placeholder="迄" style={{ width: 140 }} maxLength={12} />
        </div>
      </div>

      {/* Row 5: Need date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>需要日期[從]</span>
          <DatePicker value={needFrom} onChange={onNeedFromChange} style={{ width: 140 }} />
          <span className="text-sm">[至]</span>
          <DatePicker value={needTo} onChange={onNeedToChange} style={{ width: 140 }} />
        </div>
      </div>

      {/* Row 6: Check date 1 */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>支票到期日(1)[從]</span>
          <DatePicker value={chkDate1From} onChange={onChkDate1FromChange} style={{ width: 140 }} />
          <span className="text-sm">[至]</span>
          <DatePicker value={chkDate1To} onChange={onChkDate1ToChange} style={{ width: 140 }} />
        </div>
      </div>

      {/* Row 7: Check date 2 */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>支票到期日(2)[從]</span>
          <DatePicker value={chkDate2From} onChange={onChkDate2FromChange} style={{ width: 140 }} />
          <span className="text-sm">[至]</span>
          <DatePicker value={chkDate2To} onChange={onChkDate2ToChange} style={{ width: 140 }} />
        </div>
      </div>

      {/* Row 8: Entry employee range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_W }}>入單人員[從]</span>
          <Input value={entryEmpFrom} onChange={e => onEntryEmpFromChange(e.target.value.toUpperCase())}
            placeholder="起" style={{ width: 120 }} maxLength={8} />
          <span className="text-sm">[至]</span>
          <Input value={entryEmpTo} onChange={e => onEntryEmpToChange(e.target.value.toUpperCase())}
            placeholder="迄" style={{ width: 120 }} maxLength={8} />
        </div>
      </div>

      {/* Row 9: Approval status */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-sm font-medium" style={{ width: LABEL_W }}>核准狀態</span>
        <Radio.Group value={approvalStatus}
          onChange={(e: RadioChangeEvent) => onApprovalStatusChange(e.target.value)}>
          <Radio value={1}>已核准</Radio>
          <Radio value={2}>未核准</Radio>
          <Radio value={3}>未沖帳</Radio>
          <Radio value={4}>全部</Radio>
        </Radio.Group>
      </div>

      {/* Row 10: Report types */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-sm font-medium" style={{ width: LABEL_W }}>列印類型</span>
        <Checkbox.Group value={reportTypes}
          onChange={(checkedValues) => onReportTypesChange(checkedValues as string[])}>
          <Checkbox value="date">日期別</Checkbox>
          <Checkbox value="dept">部門別</Checkbox>
          <Checkbox value="doc">單據別</Checkbox>
          <Checkbox value="vendor">廠商別</Checkbox>
        </Checkbox.Group>
      </div>

      {/* Query button */}
      <div className="flex gap-2">
        <Button type="primary" icon={<SearchOutlined />} onClick={onQuery} loading={loading}>
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
